import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import Pagination from "./Pagination";

// --- Small debounce hook so we don't hammer the API while typing
function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// --- Column name formatting utility
function formatColumnName(columnName, capitalize = true) {
  const formatted = columnName.split("_").join(" ");
  if (capitalize) {
    // Special handling for date fields ending with "_at"
    if (columnName.endsWith("_at")) {
      const words = formatted.split(" ");
      // Capitalize first letter of first word, keep "at" lowercase
      const firstWord = words[0].charAt(0).toUpperCase() + words[0].slice(1);
      return words.length > 1 ? `${firstWord} at` : firstWord;
    }
    return formatted
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return formatted;
}

// -- Date detection utilities
function isDateWithTime(value) {
  const dateTimePatterns = [
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO 8601 datetime
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/, // PostgreSQL timestamp
  ];
  return dateTimePatterns.some((pattern) => pattern.test(String(value)));
}

function isDateOnly(value) {
  const dateOnlyPatterns = [
    /^\d{4}-\d{2}-\d{2}$/, // Date only (YYYY-MM-DD)
  ];
  return dateOnlyPatterns.some((pattern) => pattern.test(String(value)));
}

function isDateValue(value) {
  return isDateWithTime(value) || isDateOnly(value);
}

// --- Date value formatting utility
function formatDateValue(value) {
  if (!value) return value;

  try {
    if (isDateWithTime(value)) {
      return qformat(value, DATE_FORMATS.dateTime);
    }
    if (isDateOnly(value)) {
      return qformat(value, DATE_FORMATS.date);
    }
    return value;
  } catch (e) {
    // If date parsing fails, return original value
    return value;
  }
}

// --- Date utility functions
const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultValueForColumnType = (columnType) => {
  switch (columnType) {
    case "date":
      return formatLocalDate();
    case "datetime":
      return `${formatLocalDate()}T00:00`;
    default:
      return "";
  }
};

// --- Input type mapping by column type
const INPUT_TYPE_MAP = {
  date: "date",
  datetime: "datetime-local",
  number: "number",
  // text, string, boolean, and others default to "text"
};

// --- Helper to check if operator is boolean
const isBooleanOperator = (operator) => operator.includes("true") || operator.includes("false");

// --- Filter validation helper
const isValidFilter = (filter) => {
  return filter.column && filter.operator && (filter.value || isBooleanOperator(filter.operator));
};

// --- Column styling helper
const getColumnStyle = (columnType, isClickable = false) => {
  const style = isClickable ? { cursor: "pointer" } : {};

  switch (columnType) {
    case "date":
    case "datetime":
      return { ...style, width: "180px", minWidth: "180px", whiteSpace: "nowrap" };
    case "text":
      return { ...style, maxWidth: "600px", wordBreak: "break-word" };
    case "string":
      return { ...style, maxWidth: "300px", wordBreak: "break-word" };
    case "boolean":
      return { ...style, width: "120px", minWidth: "120px", textAlign: "center", whiteSpace: "nowrap" };
    case "actions":
      return { ...style, width: "120px", minWidth: "120px", textAlign: "left", whiteSpace: "nowrap" };
    default:
      return style;
  }
};

// --- Clear button helpers
const getClearButtonText = (hasQuery, hasFilters) => {
  if (hasQuery && hasFilters) return "Clear filters and search";
  if (hasFilters) return "Clear filters";
  return "Clear search";
};

// Reusable clear button component
function ClearButton({ hasQuery, hasFilters, onClear, className = "", style = {} }) {
  if (!hasQuery && !hasFilters) return null;

  return (
    <button
      type="button"
      className={className || "btn btn-link text-muted p-0"}
      onClick={onClear}
      style={style || { textDecoration: "none", fontSize: "14px" }}
    >
      {getClearButtonText(hasQuery, hasFilters)}
    </button>
  );
}

// --- Filter operators by column type
const FILTER_OPERATORS = {
  text: [
    { value: "ilike", label: "contains", placeholder: "Enter a value" },
    { value: "eq", label: "equals", placeholder: "Enter exact value" },
    { value: "not.eq", label: "not equals", placeholder: "Enter a value" },
    { value: "ilike.%*", label: "starts with", placeholder: "Enter a value" },
    { value: "ilike.*%", label: "ends with", placeholder: "Enter a value" },
  ],
  number: [
    { value: "eq", label: "equals", placeholder: "Enter number..." },
    { value: "not.eq", label: "Not equals", placeholder: "Enter number..." },
    { value: "gt", label: "greater than", placeholder: "Enter number..." },
    { value: "gte", label: "greater than or equal", placeholder: "Enter number..." },
    { value: "lt", label: "less than", placeholder: "Enter number..." },
    { value: "lte", label: "less than or equal", placeholder: "Enter number..." },
  ],
  date: [
    { value: "eq", label: "on date", placeholder: "YYYY-MM-DD" },
    { value: "gt", label: "after", placeholder: "YYYY-MM-DD" },
    { value: "lt", label: "before", placeholder: "YYYY-MM-DD" },
  ],
  boolean: [
    { value: "eq.true", label: "is true", placeholder: "" },
    { value: "eq.false", label: "is false", placeholder: "" },
  ],
  datetime: [
    { value: "gt", label: "after", placeholder: "Select date and time" },
    { value: "lt", label: "before", placeholder: "Select date and time" },
  ],
};

// --- Cell value formatting utility
function formatCellValue(value, columnType = null) {
  if (value == null || value === "") return "";

  // Handle boolean values
  if (columnType === "boolean" || typeof value === "boolean") {
    return value === true || value === "true" ? "✓" : "✗";
  }

  // Handle date values
  if (isDateValue(value)) {
    return formatDateValue(value);
  }

  return value;
}

// --- Filter components
function FilterDropdown({ columns, columnTypes, appliedFilters, onFiltersChange, getColumnDisplayName }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filters, setFilters] = useState(appliedFilters);

  useEffect(() => {
    setFilters(appliedFilters);
  }, [appliedFilters]);

  const addFilter = () => {
    const firstColumn = columns[0] || "";
    const columnType = columnTypes[firstColumn] || "text";
    const defaultOperator = FILTER_OPERATORS[columnType][0].value;
    const defaultValue = getDefaultValueForColumnType(columnType);

    const newFilter = {
      id: Date.now(),
      column: firstColumn,
      operator: defaultOperator,
      value: defaultValue,
    };
    setFilters([...filters, newFilter]);
  };

  const removeFilter = (filterId) => {
    const newFilters = filters.filter((f) => f.id !== filterId);
    setFilters(newFilters);
  };

  const updateFilter = (filterId, field, value) => {
    const newFilters = filters.map((f) => {
      if (f.id === filterId) {
        const updated = { ...f, [field]: value };
        // Reset operator and set default value when column changes
        if (field === "column") {
          const columnType = columnTypes[value] || "text";
          updated.operator = FILTER_OPERATORS[columnType][0].value;
          updated.value = getDefaultValueForColumnType(columnType);
        }
        return updated;
      }
      return f;
    });
    setFilters(newFilters);
  };

  const applyFilters = () => {
    const validFilters = filters.filter(isValidFilter);
    onFiltersChange(validFilters);
    setShowDropdown(false);
  };

  // Check if current filters differ from applied filters
  const hasFilterChanges = useMemo(() => {
    // Compare current valid filters with applied filters
    const currentValidFilters = filters.filter(isValidFilter);

    // If different lengths, there are changes
    if (currentValidFilters.length !== appliedFilters.length) {
      return true;
    }

    // Check if any filter content has changed
    return currentValidFilters.some((currentFilter) => {
      const appliedFilter = appliedFilters.find((af) => af.id === currentFilter.id);
      if (!appliedFilter) return true; // New filter

      return (
        appliedFilter.column !== currentFilter.column ||
        appliedFilter.operator !== currentFilter.operator ||
        appliedFilter.value !== currentFilter.value
      );
    });
  }, [filters, appliedFilters]);

  return (
    <Dropdown show={showDropdown} onToggle={setShowDropdown}>
      <Dropdown.Toggle
        variant="outline-secondary"
        id="filters-dropdown"
        className="d-flex align-items-center gap-2"
        style={{
          position: "relative",
          borderColor: "#dee2e6",
          backgroundColor: "#f8f9fa",
          color: "#6c757d",
          boxShadow: "none",
          transition: "all 0.15s ease-in-out",
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = "white";
          e.target.style.borderColor = "#adb5bd";
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = "#f8f9fa";
          e.target.style.borderColor = "#dee2e6";
        }}
      >
        <i className="fa fa-filter" aria-hidden="true" />
        Filters
        {appliedFilters.length > 0 && (
          <span
            className="badge bg-dark text-white rounded-circle"
            style={{
              position: "absolute",
              top: "-8px",
              right: "-8px",
              minWidth: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
            }}
          >
            {appliedFilters.length}
          </span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: "400px", padding: "16px" }}>
        {filters.length === 0 ? (
          <div className="text-center text-muted mb-3">No filters applied</div>
        ) : (
          <div className="mb-3">
            {filters.map((filter, index) => (
              <FilterRow
                key={filter.id}
                filter={filter}
                columns={columns}
                columnTypes={columnTypes}
                getColumnDisplayName={getColumnDisplayName}
                onUpdate={(field, value) => updateFilter(filter.id, field, value)}
                onRemove={() => removeFilter(filter.id)}
                isFirst={index === 0}
              />
            ))}
          </div>
        )}

        <hr className="my-3" />

        <div className="d-flex justify-content-between">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={addFilter}>
            <i className="fa fa-plus" aria-hidden="true" /> Add filter
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={applyFilters} disabled={!hasFilterChanges}>
            Apply filters
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function FilterRow({ filter, columns, columnTypes, getColumnDisplayName, onUpdate, onRemove, isFirst }) {
  const columnType = columnTypes[filter.column] || "text";
  const operators = FILTER_OPERATORS[columnType] || FILTER_OPERATORS.text;
  const selectedOperator = operators.find((op) => op.value === filter.operator) || operators[0];

  // Get default value for date/datetime inputs (using local timezone)
  const getDefaultValue = () => {
    return filter.value || getDefaultValueForColumnType(columnType);
  };

  return (
    <div className={`d-flex gap-2 align-items-center ${!isFirst ? "mt-2" : ""}`}>
      <select
        className="form-select form-select-sm bg-white"
        value={filter.column}
        onChange={(e) => onUpdate("column", e.target.value)}
        style={{ minWidth: "120px" }}
      >
        {columns.map((col) => (
          <option key={col} value={col}>
            {getColumnDisplayName(col)}
          </option>
        ))}
      </select>

      <select
        className="form-select form-select-sm bg-white"
        value={filter.operator}
        onChange={(e) => onUpdate("operator", e.target.value)}
        style={{ minWidth: "140px" }}
      >
        {operators.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </select>

      {!isBooleanOperator(selectedOperator.value) && (
        <input
          type={INPUT_TYPE_MAP[columnType] || "text"}
          className="form-control form-control-sm bg-white"
          value={getDefaultValue()}
          onChange={(e) => onUpdate("value", e.target.value)}
          placeholder={selectedOperator.placeholder}
          style={{ minWidth: "120px" }}
        />
      )}

      <button
        type="button"
        className="btn btn-sm btn-link p-1"
        onClick={onRemove}
        style={{ border: "none", background: "none", fontSize: "16px", color: "#6c757d" }}
        title="Remove filter"
      >
        <i className="fa fa-times" aria-hidden="true" />
      </button>
    </div>
  );
}

/* -----------------------------
SearchableTable

A reusable component for displaying searchable, sortable, filterable tables.
Supports different data sources through the dataAdapter interface.

## Data Adapter Interface

The dataAdapter must implement:
```javascript
{
  fetchData: async ({ query, filters, sort, page, pageSize }) => {
    // Return: { rows: [], total: number, loading?: boolean }
  }
}
```

## Usage example (.jsx)

```javascript
import SearchableTable, { createStaticDataAdapter, createPostgRESTAdapter } from './SearchableTable';

// With static data
const staticAdapter = createStaticDataAdapter({
  data: myDataArray,
  searchFields: ['name', 'email']
});

// With PostgREST
const postgrestAdapter = createPostgRESTAdapter({
  endpoint: "https://api.example.com",
  resource: "products",
  headers: { Authorization: "Bearer ..." }
});

<SearchableTable
  columns={{
    id: { type: "number" },
    name: { type: "string" },
    email: { type: "string" },
    created_at: { type: "datetime" },
  }}
  searchColumns={['name', 'email']}
  dataAdapter={staticAdapter}
  pageSize={20}
  defaultSort={{ created_at: "desc" }}
  renderCell={customRenderer}
  toolbar={<CreateButton />}
/>
```

----------------------------- */
export default function SearchableTable({
  // Data adapter implementing the fetch interface
  dataAdapter,
  // Columns to display and their types, e.g. { title: { type: "string" }, created_at: { type: "date" }, is_active: { type: "boolean", display_as: "Active" } }
  // An optional style on a column overrides its type's default width and wrapping, e.g. { ip: { type: "string", style: { whiteSpace: "nowrap" } } }
  columns = {},
  // Columns included in the full‑text-ish search (array of column names)
  searchColumns = [],
  // Field to use as unique identifier for React keys (default: 'id')
  idField = "id",
  // Page size
  pageSize = 10,
  // Optional row render override: (row, column) => ReactNode
  renderCell,
  // Optional actions column render: (row) => ReactNode
  renderActions,
  // Optional per-row class name: (row) => string | undefined
  rowClassName,
  // Optional initial sort
  defaultSort, // { columnName: "direction" } e.g. { created_at: "desc" }
  // Optional refresh trigger - increment this value to trigger a refresh
  refreshTrigger = 0,
  // Optional toolbar content (React nodes) to display alongside the search input
  toolbar = null,
  // Optional: disable pagination
  enablePagination = true,
  // Optional: disable search
  enableSearch = true,
  // Optional: disable filtering
  enableFiltering = true,
  // Optional: disable sorting
  enableSorting = true,
}) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 450);
  const [page, setPage] = useState(1); // 1-indexed
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Parse defaultSort object
  const initialSort = useMemo(() => {
    if (defaultSort) {
      const [column, direction] = Object.entries(defaultSort)[0] || [];
      return { sortBy: column, sortDir: direction || "asc" };
    }
    return { sortBy: undefined, sortDir: "asc" };
  }, [defaultSort]);

  const [sortBy, setSortBy] = useState(initialSort.sortBy);
  const [sortDir, setSortDir] = useState(initialSort.sortDir);

  // Reset sort state when defaultSort changes (e.g. tab switch)
  useEffect(() => {
    if (defaultSort) {
      const [column, direction] = Object.entries(defaultSort)[0] || [];
      setSortBy(column);
      setSortDir(direction || "asc");
    }
  }, [defaultSort]);

  const [appliedFilters, setAppliedFilters] = useState([]);

  // Helper functions to work with columns and searchColumns
  const getColumnDisplayName = (columnName, capitalize = false) => {
    const columnConfig = columns[columnName];
    const displayNameText = columnConfig && columnConfig.display_as ? columnConfig.display_as : columnName;
    return formatColumnName(displayNameText, capitalize);
  };

  // Normalize searchColumns to array format
  const normalizedSearchColumns = useMemo(() => {
    if (typeof searchColumns === "string") {
      return searchColumns
        .split(",")
        .map((col) => col.trim())
        .filter(Boolean);
    }
    if (Array.isArray(searchColumns)) {
      return searchColumns;
    }
    return [];
  }, [searchColumns]);

  function toggleSort(col) {
    if (!enableSorting || col === "actions") return; // Don't allow sorting on actions column

    if (sortBy !== col) {
      setSortBy(col);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  }

  // Extract column names and types from the columns prop
  const columnNames = useMemo(() => {
    const dataColumns = Object.keys(columns).filter((col) => !columns[col]?.hidden);
    // Add actions column if renderActions is provided
    return renderActions ? [...dataColumns, "actions"] : dataColumns;
  }, [columns, renderActions]);

  const columnTypes = useMemo(() => {
    const types = Object.fromEntries(Object.entries(columns).map(([name, config]) => [name, config?.type || "string"]));
    // Add actions column type if renderActions is provided
    if (renderActions) {
      types.actions = "actions";
    }
    return types;
  }, [columns, renderActions]);

  // Get data columns only (exclude actions) for filtering and searching
  const dataColumnNames = useMemo(() => Object.keys(columns), [columns]);

  // Separate type mapping for filter operators (normalizes string -> text, excludes actions)
  const filterColumnTypes = useMemo(() => {
    return Object.fromEntries(
      Object.entries(columnTypes)
        .filter(([name]) => name !== "actions") // Exclude actions column from filtering
        .map(([name, type]) => [name, type === "string" ? "text" : type]),
    );
  }, [columnTypes]);

  // Fetch data when parameters change
  useEffect(() => {
    if (!dataAdapter?.fetchData) {
      setError("Invalid data adapter: missing fetchData method");
      return () => {};
    }

    const ac = new AbortController();
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await dataAdapter.fetchData({
          query: debouncedQuery,
          filters: appliedFilters,
          sort: sortBy ? { column: sortBy, direction: sortDir } : null,
          page,
          pageSize,
          signal: ac.signal,
        });

        // Check if cancelled before updating state
        if (cancelled) return;

        if (result.error) {
          setError(result.error);
          return;
        }

        setRows(result.rows || []);
        setTotal(result.total ?? null);
      } catch (e) {
        if (e?.name === "AbortError" || cancelled) return;
        setError(e?.message || "Unknown error");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [dataAdapter, debouncedQuery, appliedFilters, sortBy, sortDir, page, pageSize, refreshTrigger]);

  // Reset to page 1 whenever search, sort, or filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, sortBy, sortDir, appliedFilters]);

  const pageCount = total ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  return (
    <div>
      {(enableSearch || enableFiltering || toolbar) && (
        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between my-4 gap-3">
          <div className="d-flex gap-3 align-items-center flex-sm-grow-0">
            {enableFiltering && dataColumnNames.length > 0 && (
              <FilterDropdown
                columns={dataColumnNames}
                columnTypes={filterColumnTypes}
                appliedFilters={appliedFilters}
                onFiltersChange={setAppliedFilters}
                getColumnDisplayName={getColumnDisplayName}
              />
            )}
            {enableSearch && (
              <input
                value={query}
                type="search"
                onChange={(e) => setQuery(e.target.value)}
                placeholder={(() => {
                  if (normalizedSearchColumns.length === 0) {
                    return "Type to search…";
                  }
                  if (normalizedSearchColumns.length > 3) {
                    return "Search…";
                  }
                  return `Search ${normalizedSearchColumns.map((col) => getColumnDisplayName(col)).join(", ")}…`;
                })()}
                className="form-control rounded-pill ds-input"
                style={{ fieldSizing: "content", width: "300px" }}
              />
            )}
            <ClearButton
              hasQuery={!!query}
              hasFilters={appliedFilters.length > 0}
              onClear={() => {
                setQuery("");
                setAppliedFilters([]);
              }}
            />
          </div>
          {toolbar && <div className="d-flex gap-2 align-items-center">{toolbar}</div>}
        </div>
      )}

      {error && (
        <div
          className="text-danger p-3 py-5 text-center"
          colSpan={columnNames.length || 1}
          style={{ backgroundColor: "#f9f9f9" }}
        >
          <i className="fa fa-exclamation-triangle mt-4" aria-hidden="true" style={{ fontSize: "10em" }} />
          <h2 className="mt-3">Oops. Something went wrong.</h2>
          <p className="text-muted">{error}</p>
        </div>
      )}

      {!error && loading && (
        <div
          className="text-muted p-3 py-5 text-center"
          colSpan={columnNames.length || 1}
          style={{ backgroundColor: "#f9f9f9" }}
        >
          <i className="fa fa-spinner fa-spin" style={{ fontSize: "10em", color: "#e5e5e5" }} />
          <h2 className="mt-3">Loading...</h2>
        </div>
      )}

      {!error && !loading && rows.length === 0 && (
        <div
          className="text-muted p-3 py-5 text-center"
          colSpan={columnNames.length || 1}
          style={{ backgroundColor: "#f9f9f9" }}
        >
          <i className="fa fa-list" style={{ fontSize: "10em", color: "#e5e5e5" }} />
          <h2 className="mt-3">No results found</h2>
          {(query || appliedFilters.length > 0) && (
            <div className="mt-3">
              <ClearButton
                hasQuery={!!query}
                hasFilters={appliedFilters.length > 0}
                onClear={() => {
                  setQuery("");
                  setAppliedFilters([]);
                }}
                className="btn btn-link text-primary"
                style={{ textDecoration: "none" }}
              />
            </div>
          )}
        </div>
      )}

      {!error && !loading && rows.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  {columnNames.map((col) => {
                    const colType = columnTypes[col];
                    const isActionsColumn = col === "actions";
                    const columnStyle = {
                      ...getColumnStyle(colType, enableSorting && !isActionsColumn),
                      ...columns[col]?.style,
                    };

                    return (
                      <th
                        key={col}
                        className={`fw-medium ${enableSorting && !isActionsColumn ? "user-select-none" : ""}`}
                        style={columnStyle}
                        onClick={() => toggleSort(col)}
                      >
                        <div className="d-flex align-items-center gap-1">
                          <span>{isActionsColumn ? "Actions" : getColumnDisplayName(col, true)}</span>
                          {enableSorting && sortBy === col && !isActionsColumn && (
                            <span className="small" style={{ fontSize: "0.6em" }} aria-hidden>
                              {sortDir === "asc" ? " ▲" : " ▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row[idField] || `row-${i}`} className={rowClassName ? rowClassName(row) : undefined}>
                    {columnNames.map((col) => {
                      const colType = columnTypes[col];
                      const isActionsColumn = col === "actions";
                      const cellStyle = { ...getColumnStyle(colType, false), ...columns[col]?.style };

                      return (
                        <td key={col} className="align-top" style={cellStyle}>
                          {(() => {
                            if (isActionsColumn && renderActions) {
                              return renderActions(row);
                            }
                            if (renderCell) {
                              const customRender = renderCell(row, col);
                              // If renderCell returns undefined, use default formatting
                              if (customRender !== undefined) {
                                return customRender;
                              }
                            }
                            return formatCellValue(row[col], colType);
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {enablePagination && total > pageSize && (
            <Pagination
              currentPage={page}
              totalPages={pageCount}
              onPageChange={setPage}
              loading={loading}
              total={total}
            />
          )}
        </>
      )}
    </div>
  );
}
